<template>
  <transition name="modal-fade">
    <div v-if="visible" class="modal-mask" @click.self="handleClose">
      <div class="modal-panel" role="dialog" aria-modal="true">
        <button type="button" class="close-btn" aria-label="关闭" @click="handleClose">
          <i class="bi bi-x-lg"></i>
        </button>
        <h2>个人资料</h2>

        <div v-if="loading" class="loading-text">加载中...</div>
        <template v-else-if="profile">
          <div class="profile-head">
            <div class="avatar-large">
              <img v-if="profile.avatar" :src="profile.avatar" alt="" />
              <span v-else>{{ avatarText }}</span>
            </div>
            <div class="head-meta">
              <div class="nick">{{ profile.nickName || '用户' }}</div>
              <div class="sub">头像与用户名来自微信，暂不支持修改</div>
            </div>
          </div>

          <div class="field-list">
            <div class="field-row">
              <span class="label">用户名</span>
              <span class="value readonly">{{ profile.username || '—' }}</span>
            </div>
            <div class="field-row">
              <span class="label">生日</span>
              <div class="value edit">
                <input v-model="birthdayInput" type="date" class="date-input" />
                <p class="field-tip">完善生日等资料，后续可参与送积分活动</p>
              </div>
            </div>
            <div class="field-row">
              <span class="label">微信绑定</span>
              <span class="value">
                <span class="tag" :class="{ ok: profile.wxBound }">{{ profile.wxBindStatus || '—' }}</span>
              </span>
            </div>
            <div class="field-row">
              <span class="label">上次登录</span>
              <span class="value readonly">{{ formatTime(profile.lastLoginTime) }}</span>
            </div>
          </div>

          <p v-if="errorMsg" class="error-msg">{{ errorMsg }}</p>
          <p v-if="successMsg" class="success-msg">{{ successMsg }}</p>

          <button type="button" class="btn-primary block" :disabled="saving" @click="handleSave">
            {{ saving ? '保存中...' : '保存资料' }}
          </button>
        </template>
      </div>
    </div>
  </transition>
</template>

<script>
import { getProfile, updateProfile } from '@/api/portal/member'
import { setMember } from '@/utils/auth'
import { onUserProfileOpen, offUserProfileOpen, emitProfileUpdated } from '@/utils/userMenu'

export default {
  name: 'ProfileModal',
  data() {
    return {
      visible: false,
      loading: false,
      saving: false,
      profile: null,
      birthdayInput: '',
      errorMsg: '',
      successMsg: ''
    }
  },
  computed: {
    avatarText() {
      const name = (this.profile && this.profile.nickName) || '用户'
      return name.charAt(0).toUpperCase()
    }
  },
  mounted() {
    onUserProfileOpen(this.handleOpen)
  },
  beforeDestroy() {
    offUserProfileOpen(this.handleOpen)
  },
  methods: {
    handleOpen() {
      this.visible = true
      this.errorMsg = ''
      this.successMsg = ''
      this.loadProfile()
    },
    handleClose() {
      this.visible = false
    },
    loadProfile() {
      this.loading = true
      getProfile()
        .then(res => {
          this.profile = res.data || {}
          this.birthdayInput = this.normalizeDateInput(this.profile.birthday)
          setMember(this.profile)
        })
        .catch(err => {
          this.errorMsg = err.message || '加载资料失败'
        })
        .finally(() => {
          this.loading = false
        })
    },
    normalizeDateInput(value) {
      if (!value) {
        return ''
      }
      if (typeof value === 'string') {
        return value.length >= 10 ? value.slice(0, 10) : value
      }
      return ''
    },
    formatTime(value) {
      if (!value) {
        return '—'
      }
      return String(value).replace('T', ' ').slice(0, 19)
    },
    handleSave() {
      this.errorMsg = ''
      this.successMsg = ''
      if (!this.birthdayInput) {
        this.errorMsg = '请选择生日'
        return
      }
      this.saving = true
      updateProfile({ birthday: this.birthdayInput })
        .then(res => {
          this.profile = res.data || this.profile
          setMember(this.profile)
          this.successMsg = '资料已保存'
          emitProfileUpdated()
        })
        .catch(err => {
          this.errorMsg = err.message || '保存失败'
        })
        .finally(() => {
          this.saving = false
        })
    }
  }
}
</script>

<style scoped>
.modal-mask {
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.45);
}

.modal-panel {
  position: relative;
  width: 100%;
  max-width: 480px;
  padding: 28px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
}

.close-btn {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: #6b7280;
}

.modal-panel h2 {
  margin: 0 0 20px;
  font-size: 20px;
}

.loading-text {
  text-align: center;
  color: var(--text-secondary);
  padding: 24px 0;
}

.profile-head {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eef0f4;
}

.avatar-large {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 24px;
  font-weight: 600;
  flex-shrink: 0;
}

.avatar-large img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.nick {
  font-size: 18px;
  font-weight: 600;
}

.sub {
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-muted);
}

.field-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 20px;
}

.field-row {
  display: grid;
  grid-template-columns: 88px 1fr;
  gap: 12px;
  align-items: start;
}

.label {
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 36px;
}

.value {
  font-size: 14px;
  color: var(--text-primary);
}

.value.readonly {
  line-height: 36px;
}

.date-input {
  width: 100%;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
}

.field-tip {
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--text-muted);
}

.tag {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.4;
}

.tag.ok {
  background: #ecfdf5;
  color: #059669;
}

.error-msg {
  margin: 0 0 12px;
  color: #ef4444;
  font-size: 14px;
}

.success-msg {
  margin: 0 0 12px;
  color: #059669;
  font-size: 14px;
}

.btn-primary.block {
  width: 100%;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
